// Promise-based access to ReportSaveDialog for the web build's report/export
// saves. Mirrors how the desktop awaits `save()` from
// `@tauri-apps/plugin-dialog`, so both branches of a save read the same:
//
//   const filename = await promptReportSave({ ... });
//   if (!filename) return;            // cancelled
//
// The dialog is mounted on its own React root attached to <body> rather than
// being wired into App's dialog state, because the callers are plain utils
// (builtinReports, excelReport) and components that live in SEPARATE browser
// windows (the diagnostics and export tool windows) — each would otherwise need
// its own copy of the state plumbing.
//
// The format chosen for a given save is remembered (localStorage) so a user who
// always wants .odt picks it once, in the spirit of pickerFolder.ts's
// last-folder memory for the desktop dialogs.

import { createRoot } from "react-dom/client";
import { ReportSaveDialog } from "../components/dialogs/ReportSaveDialog";
import { splitFilename, type ReportFormat } from "./reportFormats";

const FORMAT_KEY_PREFIX = "tiger.reportFormat.";

/** The extension this save was last completed with, if any. */
export function lastReportFormat(key: string): string | undefined {
  try {
    return globalThis.localStorage?.getItem(FORMAT_KEY_PREFIX + key) || undefined;
  } catch {
    return undefined; // storage disabled (private mode) — fall back to the default
  }
}

/** Remember the chosen extension for next time. */
export function rememberReportFormat(key: string, ext: string): void {
  try {
    globalThis.localStorage?.setItem(FORMAT_KEY_PREFIX + key, ext);
  } catch {
    // ignore: a remembered format is a convenience, not a requirement
  }
}

export interface ReportSavePromptOptions {
  /** Suggested filename WITH extension — the same default the desktop passes to
   *  `save({ defaultPath })`. Its extension seeds the format when nothing is
   *  remembered yet. */
  defaultName: string;
  /** Formats to offer (utils/reportFormats.ts). */
  formats: ReportFormat[];
  /** Key the chosen format is remembered under; use a `PickerKey` so the web
   *  format memory and the desktop folder memory share one naming scheme. */
  rememberKey: string;
  /** Title-bar resource id. */
  titleId?: string;
}

/**
 * Ask for the output filename + format. Resolves `<base>.<ext>` or `null` when
 * the user cancels. Web build only — the desktop uses its native save dialog.
 */
export function promptReportSave(opts: ReportSavePromptOptions): Promise<string | null> {
  const { defaultName, formats, rememberKey, titleId = "TIGER_SAVE_REPORT_TITLE" } = opts;
  const { base, ext } = splitFilename(defaultName);
  const defaultExt = lastReportFormat(rememberKey) ?? ext;

  return new Promise<string | null>((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);

    let settled = false;
    const finish = (filename: string | null) => {
      if (settled) return;
      settled = true;
      if (filename) rememberReportFormat(rememberKey, splitFilename(filename).ext);
      // Unmount after the current React commit, not during the click handler.
      setTimeout(() => {
        root.unmount();
        container.remove();
      }, 0);
      resolve(filename);
    };

    root.render(
      <ReportSaveDialog
        open
        titleId={titleId}
        defaultBase={base}
        formats={formats}
        defaultExt={defaultExt}
        onClose={() => finish(null)}
        onSubmit={(filename) => finish(filename)}
      />,
    );
  });
}
